import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import { FaCheck } from 'react-icons/fa';
import '../../styles/RegisterTrainerAndSchool.css';

const RegisterTrainer = () => {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const STEPS = [
        { key: 'trainer', label: t('register.steps.yourData') },
        { key: 'school', label: t('register.steps.schoolData') },
    ];

    const [trainerData, setTrainerData] = useState({
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        city: '',
        postalCode: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        statuteAccepted: false,
    });

    const [schoolData, setSchoolData] = useState({
        name: '',
        country: '',
        city: '',
        postalCode: '',
        street: '',
        buildingNumber: '',
        apartmentNumber: '',
        phone: '',
        email: '',
    });

    const [sameAddress, setSameAddress] = useState(false);

    const handleTrainerChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setTrainerData({ ...trainerData, [e.target.name]: value });
        if (fieldErrors[e.target.name]) {
            setFieldErrors({ ...fieldErrors, [e.target.name]: '' });
        }
    };

    const handleSchoolChange = (e) => {
        setSchoolData({ ...schoolData, [e.target.name]: e.target.value });
        if (fieldErrors[`school_${e.target.name}`]) {
            setFieldErrors({ ...fieldErrors, [`school_${e.target.name}`]: '' });
        }
    };

    const handleSameAddressToggle = () => {
        const newValue = !sameAddress;
        setSameAddress(newValue);
        if (newValue) {
            setSchoolData(prev => ({
                ...prev,
                country: trainerData.country,
                city: trainerData.city,
                postalCode: trainerData.postalCode,
                street: trainerData.street,
                buildingNumber: trainerData.buildingNumber,
                apartmentNumber: trainerData.apartmentNumber,
            }));
        }
    };

    const validateTrainerStep = () => {
        const errors = {};

        if (!trainerData.email || !trainerData.email.trim()) {
            errors.email = t('register.validation.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trainerData.email)) {
            errors.email = t('register.validation.emailInvalid');
        }

        if (!trainerData.password) {
            errors.password = t('register.validation.passwordRequired');
        } else if (trainerData.password.length < 8) {
            errors.password = t('register.validation.passwordMin');
        } else if (trainerData.password.length > 100) {
            errors.password = t('register.validation.passwordMax');
        }

        if (!trainerData.firstName || !trainerData.firstName.trim()) {
            errors.firstName = t('register.validation.firstNameRequired');
        } else if (trainerData.firstName.trim().length < 2) {
            errors.firstName = t('register.validation.firstNameMin');
        }

        if (!trainerData.lastName || !trainerData.lastName.trim()) {
            errors.lastName = t('register.validation.lastNameRequired');
        } else if (trainerData.lastName.trim().length < 2) {
            errors.lastName = t('register.validation.lastNameMin');
        }

        if (!trainerData.phone || !trainerData.phone.trim()) {
            errors.phone = t('register.validation.phoneRequired');
        } else if (!/^[0-9]+$/.test(trainerData.phone)) {
            errors.phone = t('register.validation.phoneOnlyDigits');
        } else if (trainerData.phone.length < 9 || trainerData.phone.length > 15) {
            errors.phone = t('register.validation.phoneLength');
        }

        if (!trainerData.country || !trainerData.country.trim()) errors.country = t('register.validation.countryRequired');
        if (!trainerData.city || !trainerData.city.trim()) errors.city = t('register.validation.cityRequired');
        if (!trainerData.postalCode || !trainerData.postalCode.trim()) errors.postalCode = t('register.validation.postalCodeRequired');
        if (!trainerData.street || !trainerData.street.trim()) errors.street = t('register.validation.streetRequired');
        if (!trainerData.buildingNumber || !trainerData.buildingNumber.trim()) errors.buildingNumber = t('register.validation.buildingNumberRequired');

        if (!trainerData.statuteAccepted) {
            errors.statuteAccepted = t('register.validation.statuteRequired');
        }

        return errors;
    };

    const validateSchoolStep = () => {
        const errors = {};

        if (!schoolData.name || !schoolData.name.trim()) {
            errors.school_name = t('register.validation.schoolNameRequired');
        }

        if (!schoolData.email || !schoolData.email.trim()) {
            errors.school_email = t('register.validation.schoolEmailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(schoolData.email)) {
            errors.school_email = t('register.validation.emailInvalid');
        }

        if (!schoolData.phone || !schoolData.phone.trim()) {
            errors.school_phone = t('register.validation.phoneRequired');
        } else if (!/^[0-9]+$/.test(schoolData.phone)) {
            errors.school_phone = t('register.validation.phoneOnlyDigits');
        } else if (schoolData.phone.length < 9 || schoolData.phone.length > 15) {
            errors.school_phone = t('register.validation.phoneLength');
        }

        if (!sameAddress) {
            if (!schoolData.country || !schoolData.country.trim()) errors.school_country = t('register.validation.countryRequired');
            if (!schoolData.city || !schoolData.city.trim()) errors.school_city = t('register.validation.cityRequired');
            if (!schoolData.postalCode || !schoolData.postalCode.trim()) errors.school_postalCode = t('register.validation.postalCodeRequired');
            if (!schoolData.street || !schoolData.street.trim()) errors.school_street = t('register.validation.streetRequired');
            if (!schoolData.buildingNumber || !schoolData.buildingNumber.trim()) errors.school_buildingNumber = t('register.validation.buildingNumberRequired');
        }

        return errors;
    };

    const handleNext = (e) => {
        e.preventDefault();
        setError('');

        const errors = validateTrainerStep();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError(t('register.validation.fixErrors'));
            return;
        }

        setFieldErrors({});
        setCurrentStep(1);
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        if (currentStep === 0) {
            navigate(-1);
        } else {
            setCurrentStep(0);
            setError('');
            setFieldErrors({});
            window.scrollTo(0, 0);
        }
    };

    const mapBackendErrors = (errors) => {
        const mappedErrors = {};
        Object.keys(errors).forEach(key => {
            if (key.startsWith('address.')) {
                const fieldName = key.replace('address.', '');
                mappedErrors[fieldName] = errors[key];
            } else {
                mappedErrors[key] = errors[key];
            }
        });
        return mappedErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setFieldErrors({});

        const errors = validateSchoolStep();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setError(t('register.validation.fixErrors'));
            return;
        }

        setLoading(true);

        const finalSchoolData = sameAddress
            ? {
                ...schoolData,
                country: trainerData.country,
                city: trainerData.city,
                postalCode: trainerData.postalCode,
                street: trainerData.street,
                buildingNumber: trainerData.buildingNumber,
                apartmentNumber: trainerData.apartmentNumber,
            }
            : schoolData;

        try {
            const trainerRequest = {
                password: trainerData.password,
                firstName: trainerData.firstName,
                lastName: trainerData.lastName,
                email: trainerData.email,
                phone: trainerData.phone,
                statuteAccepted: trainerData.statuteAccepted,
                address: {
                    country: trainerData.country,
                    city: trainerData.city,
                    postalCode: trainerData.postalCode,
                    street: trainerData.street,
                    buildingNumber: trainerData.buildingNumber,
                    apartmentNumber: trainerData.apartmentNumber,
                }
            };

            const trainerResponse = await axios.post('/api/v1/auth/register/trainer', trainerRequest);
            const trainerId = trainerResponse.data;

            const schoolRequest = {
                name: finalSchoolData.name,
                address: {
                    country: finalSchoolData.country,
                    city: finalSchoolData.city,
                    postalCode: finalSchoolData.postalCode,
                    street: finalSchoolData.street,
                    buildingNumber: finalSchoolData.buildingNumber,
                    apartmentNumber: finalSchoolData.apartmentNumber,
                },
                phone: finalSchoolData.phone,
                email: finalSchoolData.email,
            };

            await axios.post(`/api/v1/school/create/${trainerId}`, schoolRequest);

            navigate('/registration-success');
        } catch (err) {
            console.error('Registration error:', err);
            if (err.response?.data?.errors) {
                const backendErrors = err.response.data.errors;
                const mappedErrors = mapBackendErrors(backendErrors);

                const trainerFields = ['email', 'password', 'firstName', 'lastName', 'phone',
                    'country', 'city', 'postalCode', 'street', 'buildingNumber', 'apartmentNumber',
                    'statuteAccepted', 'username'];
                const hasTrainerErrors = Object.keys(mappedErrors).some(key => trainerFields.includes(key));

                if (hasTrainerErrors) {
                    setCurrentStep(0);
                    setFieldErrors(mappedErrors);
                    setError(err.response.data.message || t('register.validation.fixErrors'));
                    return;
                }

                const schoolFieldErrors = {};
                Object.keys(mappedErrors).forEach(key => {
                    schoolFieldErrors[`school_${key}`] = mappedErrors[key];
                });
                setFieldErrors(schoolFieldErrors);
                setError(err.response.data.message || t('register.validation.fixErrors'));
            } else if (err.response?.data?.message) {
                setError(err.response.data.message);
            } else {
                setError(t('register.genericError'));
            }
        } finally {
            setLoading(false);
        }
    };

    const renderStepIndicator = () => (
        <div className="reg__steps">
            {STEPS.map((step, index) => (
                <React.Fragment key={step.key}>
                    <div
                        className={`reg__step ${index === currentStep ? 'reg__step--active' : ''} ${index < currentStep ? 'reg__step--done' : ''}`}
                        onClick={index < currentStep ? () => { setCurrentStep(index); setError(''); setFieldErrors({}); } : undefined}
                    >
                        <div className="reg__step-circle">
                            {index < currentStep ? <FaCheck size={12} /> : index + 1}
                        </div>
                        <span className="reg__step-label">{step.label}</span>
                    </div>
                    {index < STEPS.length - 1 && (
                        <div className={`reg__step-line ${index < currentStep ? 'reg__step-line--done' : ''}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    const renderTrainerForm = () => (
        <div className="reg__form-section">
            <h2 className="reg__section-title">{t('register.personalData')}</h2>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('register.email')}</label>
                    <input
                        type="email"
                        name="email"
                        placeholder={t('register.emailPlaceholder')}
                        className={`reg__input ${fieldErrors.email ? 'reg__input--error' : ''}`}
                        value={trainerData.email}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.email && <span className="reg__field-error">{fieldErrors.email}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.password')}</label>
                    <input
                        type="password"
                        name="password"
                        placeholder={t('register.passwordPlaceholder')}
                        className={`reg__input ${fieldErrors.password ? 'reg__input--error' : ''}`}
                        value={trainerData.password}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.password && <span className="reg__field-error">{fieldErrors.password}</span>}
                </div>
            </div>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('register.firstName')}</label>
                    <input
                        type="text"
                        name="firstName"
                        placeholder={t('register.firstNamePlaceholder')}
                        className={`reg__input ${fieldErrors.firstName ? 'reg__input--error' : ''}`}
                        value={trainerData.firstName}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.firstName && <span className="reg__field-error">{fieldErrors.firstName}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.lastName')}</label>
                    <input
                        type="text"
                        name="lastName"
                        placeholder={t('register.lastNamePlaceholder')}
                        className={`reg__input ${fieldErrors.lastName ? 'reg__input--error' : ''}`}
                        value={trainerData.lastName}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.lastName && <span className="reg__field-error">{fieldErrors.lastName}</span>}
                </div>
            </div>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('register.phone')}</label>
                    <input
                        type="text"
                        name="phone"
                        placeholder={t('register.phonePlaceholder')}
                        className={`reg__input ${fieldErrors.phone ? 'reg__input--error' : ''}`}
                        value={trainerData.phone}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.phone && <span className="reg__field-error">{fieldErrors.phone}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.country')}</label>
                    <input
                        type="text"
                        name="country"
                        placeholder={t('register.countryPlaceholder')}
                        className={`reg__input ${fieldErrors.country ? 'reg__input--error' : ''}`}
                        value={trainerData.country}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.country && <span className="reg__field-error">{fieldErrors.country}</span>}
                </div>
            </div>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('register.city')}</label>
                    <input
                        type="text"
                        name="city"
                        placeholder={t('register.cityPlaceholder')}
                        className={`reg__input ${fieldErrors.city ? 'reg__input--error' : ''}`}
                        value={trainerData.city}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.city && <span className="reg__field-error">{fieldErrors.city}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.postalCode')}</label>
                    <input
                        type="text"
                        name="postalCode"
                        placeholder={t('register.postalCodePlaceholder')}
                        className={`reg__input ${fieldErrors.postalCode ? 'reg__input--error' : ''}`}
                        value={trainerData.postalCode}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.postalCode && <span className="reg__field-error">{fieldErrors.postalCode}</span>}
                </div>
            </div>

            <div className="reg__row reg__row--three">
                <div className="reg__field">
                    <label className="reg__label">{t('register.street')}</label>
                    <input
                        type="text"
                        name="street"
                        placeholder={t('register.streetPlaceholder')}
                        className={`reg__input ${fieldErrors.street ? 'reg__input--error' : ''}`}
                        value={trainerData.street}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.street && <span className="reg__field-error">{fieldErrors.street}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.buildingNumber')}</label>
                    <input
                        type="text"
                        name="buildingNumber"
                        placeholder={t('register.buildingNumberPlaceholder')}
                        className={`reg__input ${fieldErrors.buildingNumber ? 'reg__input--error' : ''}`}
                        value={trainerData.buildingNumber}
                        onChange={handleTrainerChange}
                    />
                    {fieldErrors.buildingNumber && <span className="reg__field-error">{fieldErrors.buildingNumber}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('register.apartmentNumber')}</label>
                    <input
                        type="text"
                        name="apartmentNumber"
                        placeholder={t('register.apartmentNumberPlaceholder')}
                        className="reg__input"
                        value={trainerData.apartmentNumber}
                        onChange={handleTrainerChange}
                    />
                </div>
            </div>

            <div className="reg__checkbox-group">
                <label className="reg__checkbox-label">
                    <input
                        type="checkbox"
                        name="statuteAccepted"
                        checked={trainerData.statuteAccepted}
                        onChange={handleTrainerChange}
                        className="reg__checkbox"
                    />
                    <span>{t('register.acceptTermsWithLink')}</span>
                </label>
                {fieldErrors.statuteAccepted && <span className="reg__field-error">{fieldErrors.statuteAccepted}</span>}
            </div>
        </div>
    );

    const renderSchoolForm = () => (
        <div className="reg__form-section">
            <h2 className="reg__section-title">{t('school.createSubtitle')}</h2>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('school.name')}</label>
                    <input
                        type="text"
                        name="name"
                        placeholder={t('school.namePlaceholder')}
                        className={`reg__input ${fieldErrors.school_name ? 'reg__input--error' : ''}`}
                        value={schoolData.name}
                        onChange={handleSchoolChange}
                    />
                    {fieldErrors.school_name && <span className="reg__field-error">{fieldErrors.school_name}</span>}
                </div>
                <div className="reg__field">
                    <label className="reg__label">{t('school.email')}</label>
                    <input
                        type="email"
                        name="email"
                        placeholder={t('school.emailPlaceholder')}
                        className={`reg__input ${fieldErrors.school_email ? 'reg__input--error' : ''}`}
                        value={schoolData.email}
                        onChange={handleSchoolChange}
                    />
                    {fieldErrors.school_email && <span className="reg__field-error">{fieldErrors.school_email}</span>}
                </div>
            </div>

            <div className="reg__row">
                <div className="reg__field">
                    <label className="reg__label">{t('school.phone')}</label>
                    <input
                        type="text"
                        name="phone"
                        placeholder={t('register.phonePlaceholder')}
                        className={`reg__input ${fieldErrors.school_phone ? 'reg__input--error' : ''}`}
                        value={schoolData.phone}
                        onChange={handleSchoolChange}
                    />
                    {fieldErrors.school_phone && <span className="reg__field-error">{fieldErrors.school_phone}</span>}
                </div>
                <div className="reg__field" />
            </div>

            <div className="reg__same-address">
                <label className="reg__checkbox-label">
                    <input
                        type="checkbox"
                        checked={sameAddress}
                        onChange={handleSameAddressToggle}
                        className="reg__checkbox"
                    />
                    <span>{t('register.sameAddress')}</span>
                </label>
            </div>

            {!sameAddress && (
                <>
                    <div className="reg__row">
                        <div className="reg__field">
                            <label className="reg__label">{t('register.country')}</label>
                            <input
                                type="text"
                                name="country"
                                placeholder={t('register.countryPlaceholder')}
                                className={`reg__input ${fieldErrors.school_country ? 'reg__input--error' : ''}`}
                                value={schoolData.country}
                                onChange={handleSchoolChange}
                            />
                            {fieldErrors.school_country && <span className="reg__field-error">{fieldErrors.school_country}</span>}
                        </div>
                        <div className="reg__field">
                            <label className="reg__label">{t('register.city')}</label>
                            <input
                                type="text"
                                name="city"
                                placeholder={t('register.cityPlaceholder')}
                                className={`reg__input ${fieldErrors.school_city ? 'reg__input--error' : ''}`}
                                value={schoolData.city}
                                onChange={handleSchoolChange}
                            />
                            {fieldErrors.school_city && <span className="reg__field-error">{fieldErrors.school_city}</span>}
                        </div>
                    </div>

                    <div className="reg__row">
                        <div className="reg__field">
                            <label className="reg__label">{t('register.postalCode')}</label>
                            <input
                                type="text"
                                name="postalCode"
                                placeholder={t('register.postalCodePlaceholder')}
                                className={`reg__input ${fieldErrors.school_postalCode ? 'reg__input--error' : ''}`}
                                value={schoolData.postalCode}
                                onChange={handleSchoolChange}
                            />
                            {fieldErrors.school_postalCode && <span className="reg__field-error">{fieldErrors.school_postalCode}</span>}
                        </div>
                        <div className="reg__field">
                            <label className="reg__label">{t('register.street')}</label>
                            <input
                                type="text"
                                name="street"
                                placeholder={t('register.streetPlaceholder')}
                                className={`reg__input ${fieldErrors.school_street ? 'reg__input--error' : ''}`}
                                value={schoolData.street}
                                onChange={handleSchoolChange}
                            />
                            {fieldErrors.school_street && <span className="reg__field-error">{fieldErrors.school_street}</span>}
                        </div>
                    </div>

                    <div className="reg__row">
                        <div className="reg__field">
                            <label className="reg__label">{t('register.buildingNumber')}</label>
                            <input
                                type="text"
                                name="buildingNumber"
                                placeholder={t('register.buildingNumberPlaceholder')}
                                className={`reg__input ${fieldErrors.school_buildingNumber ? 'reg__input--error' : ''}`}
                                value={schoolData.buildingNumber}
                                onChange={handleSchoolChange}
                            />
                            {fieldErrors.school_buildingNumber && <span className="reg__field-error">{fieldErrors.school_buildingNumber}</span>}
                        </div>
                        <div className="reg__field">
                            <label className="reg__label">{t('register.apartmentNumber')}</label>
                            <input
                                type="text"
                                name="apartmentNumber"
                                placeholder={t('register.apartmentNumberPlaceholder')}
                                className="reg__input"
                                value={schoolData.apartmentNumber}
                                onChange={handleSchoolChange}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="reg">
            <div className="reg__left-panel">
                <div className="reg__left-content">
                    <h2 className="reg__welcome-text">
                        {currentStep === 0
                            ? <>{t('register.trainerWelcome.createAccount')}<span className="reg__welcome-highlight">{t('register.trainerWelcome.andCreateSchool')}</span></>
                            : <>{t('register.trainerWelcome.almostDone')}<span className="reg__welcome-highlight">{t('register.trainerWelcome.lastStep')}</span></>
                        }
                    </h2>
                </div>
            </div>
            <div className="reg__right-panel">
                <button className="reg__back-btn" onClick={handleBack} type="button">
                    {t('register.back')}
                </button>

                <div className="reg__form-wrapper">
                    {renderStepIndicator()}

                    <form
                        className="reg__form"
                        onSubmit={currentStep === 0 ? handleNext : handleSubmit}
                    >
                        <div className="reg__step-content">
                            {currentStep === 0 ? renderTrainerForm() : renderSchoolForm()}
                        </div>

                        {error && <div className="reg__error">{error}</div>}

                        <div className="reg__actions">
                            {currentStep > 0 && (
                                <button
                                    type="button"
                                    className="reg__back-step-btn"
                                    onClick={handleBack}
                                >
                                    {t('register.backStep')}
                                </button>
                            )}
                            <button
                                type="submit"
                                className={`reg__submit-btn ${currentStep === STEPS.length - 1 ? 'reg__submit-btn--final' : ''}`}
                                disabled={loading}
                            >
                                {loading
                                    ? t('register.registering')
                                    : currentStep === STEPS.length - 1
                                        ? t('register.registerButton')
                                        : t('register.nextStep')
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RegisterTrainer;
